import { useState, type FormEvent, type ReactNode } from "react";
import * as RD from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Button } from "./Button";
import { cx } from "../lib/cx";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "md" | "lg";
  /** Painel lateral em vez de janela central. */
  sheet?: boolean;
  /** Quando presente, o corpo vira um <form> e Enter envia. */
  onSubmit?: () => void;
}

export function Dialog({ open, onOpenChange, title, description, children, footer, size = "md", sheet, onSubmit }: DialogProps) {
  const body = (
    <>
      <div className="vx-dialog__body">{children}</div>
      {footer ? <div className="vx-dialog__foot">{footer}</div> : null}
    </>
  );
  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="vx-overlay" />
        <RD.Content
          className={cx(sheet ? "vx-sheet" : "vx-dialog", !sheet && size === "lg" && "vx-dialog--lg")}
          {...(description ? {} : { "aria-describedby": undefined })}
        >
          <div className="vx-dialog__head">
            <div>
              <RD.Title className="vx-dialog__title">{title}</RD.Title>
              {description ? <RD.Description className="vx-dialog__desc">{description}</RD.Description> : null}
            </div>
            <span className="vx-spacer" />
            <RD.Close asChild>
              <Button variant="ghost" size="sm" iconOnly label="Fechar" icon={<X />} />
            </RD.Close>
          </div>
          {onSubmit ? (
            <form
              style={{ display: "contents" }}
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                onSubmit();
              }}
              noValidate
            >
              {body}
            </form>
          ) : (
            body
          )}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

// ---------------------------------------------------------------- confirmação

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  /** Ação destrutiva: exige digitar `confirmText` exatamente para liberar o botão. */
  destructive?: boolean;
  confirmText?: string;
  /** Pede um motivo (obrigatório com `reasonMin` caracteres). */
  reason?: { label: string; min: number; max?: number; hint?: string };
  onConfirm: (reason: string) => Promise<unknown> | unknown;
}

export function ConfirmDialog({
  open, onOpenChange, title, children, confirmLabel, destructive, confirmText, reason, onConfirm,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState("");
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const needsTyping = destructive && confirmText;
  const typedOk = !needsTyping || typed.trim() === confirmText;
  const reasonOk = !reason || (why.trim().length >= reason.min && (!reason.max || why.length <= reason.max));

  async function confirm() {
    if (!typedOk || !reasonOk) return;
    setBusy(true);
    try {
      await onConfirm(why.trim());
      setTyped("");
      setWhy("");
      onOpenChange(false);
    } catch {
      /* o chamador mostra o erro (toast); a janela fica aberta */
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setTyped("");
          setWhy("");
        }
        onOpenChange(o);
      }}
      title={title}
      onSubmit={confirm}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Voltar</Button>
          <Button type="submit" variant={destructive ? "danger-solid" : "primary"} disabled={!typedOk || !reasonOk} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="vx-stack">
        {children}
        {reason ? (
          <div className="vx-field">
            <label className="vx-field__label" htmlFor="vx-confirm-reason">
              {reason.label}
            </label>
            <textarea
              id="vx-confirm-reason"
              className="vx-textarea"
              value={why}
              maxLength={reason.max}
              onChange={(e) => setWhy(e.target.value)}
              aria-describedby="vx-confirm-reason-hint"
            />
            <span className="vx-field__hint" id="vx-confirm-reason-hint">
              {reason.hint ?? `Mínimo de ${reason.min} caracteres.`} {why.trim().length}/{reason.max ?? reason.min}
            </span>
          </div>
        ) : null}
        {needsTyping ? (
          <div className="vx-field">
            <label className="vx-field__label" htmlFor="vx-confirm-typed">
              Para confirmar, digite <strong className="mono">{confirmText}</strong>
            </label>
            <input
              id="vx-confirm-typed"
              className="vx-input mono"
              value={typed}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setTyped(e.target.value)}
            />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
